import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Spinner } from '@components/shared/Spinner';
import theme from '@themes/theme';

export type PurchaseStatus = 'pending' | 'success' | 'cancelled';

interface PurchaseStatusOverlayProps {
  status: PurchaseStatus;
}

/**
 * Full-screen, non-interactive status hint shown while a RevenueCat
 * purchase is in flight (TA-10254 QA finding: no feedback between the
 * SubscribeModal closing and the native purchase sheet resolving). Mounted
 * once at the RevenueCatProvider level so every SubscribeModal caller gets
 * it for free instead of each screen wiring its own copy.
 */
export function PurchaseStatusOverlay({
  status,
}: Readonly<PurchaseStatusOverlayProps>) {
  const { t } = useTranslation('models');

  return (
    <Box
      position='absolute'
      top={0}
      left={0}
      right={0}
      bottom={0}
      alignItems='center'
      justifyContent='center'
      backgroundColor='scrimDark50'
      pointerEvents='none'
      zIndex={70}>
      <Box
        flexDirection='row'
        alignItems='center'
        backgroundColor='transparentBlack'
        borderRadius='m'
        paddingHorizontal='l'
        paddingVertical='s'>
        {status === 'pending' && (
          <Spinner size={18} strokeWidth={2} color={theme.colors.white} />
        )}
        {status === 'success' && (
          <Icon name='Check' color='white' variant='m' />
        )}
        {status === 'cancelled' && (
          <Icon name='AlertTriangle' color='white' variant='m' />
        )}
        <Text variant='webSmall' color='white' ml='xs'>
          {t(`payments.purchaseStatus.${status}`)}
        </Text>
      </Box>
    </Box>
  );
}
